/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Racine de l'application : les deux sites vivent dans le meme bundle.
 *
 * `/tchad/*` et `/*` sont deux coquilles distinctes, chacune avec son
 * fournisseur de langue, son en-tete, son pied de page et son jeu de routes.
 * Elles ne partagent que le routeur et quelques composants transverses
 * (`Wordmark`, `BackToTop`, `SEO`, les pages juridiques) : les chartes
 * graphiques different trop pour qu'un gabarit commun tienne.
 *
 * L'ordre des routes compte : `/tchad/*` doit passer avant `/*`, sinon la
 * coquille UK, dont le motif est plus large, capterait aussi le site Tchad.
 */
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider, useLang } from "./i18n/LanguageContext";
import {
  LanguageProvider as TchadLanguageProvider,
  useLanguage as useTchadLanguage,
} from "./tchad/contexts/LanguageContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Loading from "./components/Loading";
import BackToTop from "./components/BackToTop";
import { Toaster } from "./components/ui/sonner";

import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import ProductDetail from "./pages/ProductDetail";
import Projects from "./pages/Projects";
import Careers from "./pages/Careers";
import Contact from "./pages/Contact";
import Legal from "./pages/Legal";

import { Header as TchadHeader } from "./tchad/components/Header";
import { Footer as TchadFooter } from "./tchad/components/Footer";
import { Home as TchadHome } from "./tchad/pages/Home";
import { About as TchadAbout } from "./tchad/pages/About";
import { Services as TchadServices } from "./tchad/pages/Services";
import { ServiceDetail as TchadServiceDetail } from "./tchad/pages/ServiceDetail";
import { Projects as TchadProjects } from "./tchad/pages/Projects";
import { Careers as TchadCareers } from "./tchad/pages/Careers";
import { Contact as TchadContact } from "./tchad/pages/Contact";
import { Legal as TchadLegal } from "./tchad/pages/Legal";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const UKShell = () => {
  const { loading, t, lang } = useLang();
  if (loading || !t) return <Loading />;
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/products/:slug" element={<ProductDetail />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Legal doc="privacy" />} />
        <Route path="/terms" element={<Legal doc="terms" />} />
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
      <BackToTop variant="uk" lang={lang} />
      <Toaster richColors position="top-right" />
    </>
  );
};

const TchadShell = () => {
  const { language } = useTchadLanguage();
  return (
  <div className="tchad-scope min-h-screen flex flex-col">
    <ScrollToTop />
    <TchadHeader />
    <Routes>
      <Route path="/" element={<TchadHome />} />
      <Route path="/about" element={<TchadAbout />} />
      <Route path="/services" element={<TchadServices />} />
      <Route path="/services/:slug" element={<TchadServiceDetail />} />
      <Route path="/projects" element={<TchadProjects />} />
      <Route path="/careers" element={<TchadCareers />} />
      <Route path="/contact" element={<TchadContact />} />
      <Route path="/privacy" element={<TchadLegal doc="privacy" />} />
      <Route path="/terms" element={<TchadLegal doc="terms" />} />
    </Routes>
    <TchadFooter />
    <BackToTop variant="tchad" lang={language} />
    <Toaster position="top-center" />
  </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/tchad/*"
          element={
            <TchadLanguageProvider>
              <TchadShell />
            </TchadLanguageProvider>
          }
        />
        <Route
          path="/*"
          element={
            <div className="App min-h-screen flex flex-col">
              <LanguageProvider>
                <UKShell />
              </LanguageProvider>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
